import { createContext, type CSSProperties, type PropsWithChildren, type RefObject, useContext } from 'react';

import type { Color } from '../../../types/colors';
import type { ItemSelection } from '../../../types/components';
import type { Purpose, Size } from '../../../types/layout';
import type { SgPickerItem } from './types';

/**
 * SGPICKER CONTEXT INTERFACE
 * ==========================
 *
 * Central context that manages all state and handlers for SgPicker components.
 * This allows child components to access picker state without excessive prop drilling.
 *
 * The context is organized by component concerns:
 * - Core configuration (color, size, purpose)
 * - Menu-related props (items, selection, dropdown state)
 * - Input-related props (value, handlers, focus state)
 * - Action props (clear, badges, custom items)
 * - External refs for imperative access
 */
export interface SgPickerInputContextValue<T extends string | number = string | number> {
  /** Custom item creation */
  showInputAsCustomItem?: boolean;
  createCustomItemButtonLabel?: string;
  purpose: Purpose;
  color: Color;
  size: Size;
  canCreateCustomItem: boolean;
  isLoading: boolean;
  inputValue: string;
  createCustomItemHandler: (label: string) => void;
  showNoResultLabel?: boolean;
  showTypingHintLabel?: boolean;
  onChange?: (items: SgPickerItem<T>) => void;

  /** Menu/dropdown state and handlers */
  isOpen?: boolean;
  items?: SgPickerItem<T>[];
  itemSelection?: ItemSelection;
  selectedItems?: SgPickerItem<T>[];
  selectedItem?: SgPickerItem<T> | null;
  highlightedIndex?: number | null;
  getMenuProps: (
    options?: Record<string, unknown>,
    otherOptions?: { suppressRefError?: boolean },
  ) => Record<string, unknown>;
  getItemProps: (options: { index: number; item: SgPickerItem<T> }) => Record<string, unknown>;
  getToggleButtonProps: () => Record<string, unknown>;
  refs: { setFloating: (node: HTMLElement | null) => void; setReference: (node: HTMLElement | null) => void };
  floatingStyles?: CSSProperties;
  maxSelectionState?: 'normal' | 'warning' | 'error';
  maxSelectionMessage?: string;
  canSelectAll?: boolean;
  hasTooManyResults?: boolean;
  onSelectAll?: () => void;
  onUnselectAll?: () => void;

  /** Input field configuration and handlers */
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  inputId?: string;
  labelledBy?: string;
  getInputProps?: (props: Record<string, unknown>) => Record<string, unknown>;
  onPaste?: (pastedText: string) => void;
  onBlurInput?: () => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBackspaceRemove?: () => void;
  setFocused?: (focused: boolean) => void;

  /** Clear button configuration */
  showClearButton?: boolean;
  onClear?: () => void;
  isFocused?: boolean;
  isHover?: boolean;

  /** Badge management for multiple selection */
  removeSelectedItem?: (item: SgPickerItem<T>) => void;

  /** External refs for imperative access */
  inputRef?: RefObject<HTMLInputElement | null>;
  dropdownRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Context instance with flexible typing to support both string and number values.
 * Uses any type for maximum flexibility while maintaining type safety at usage sites.
 */
// oxlint-disable-next-line no-explicit-any -- Context needs to be flexible for different value types
const SgPickerInputContext = createContext<SgPickerInputContextValue<any> | null>(null);

/**
 * CONTEXT HOOK
 * ============
 *
 * Custom hook for accessing SgPicker context with proper error handling.
 * Throws descriptive error if used outside provider scope.
 *
 * @returns Typed context value based on generic parameter
 */
export const useSgPickerInputContext = <T extends string | number = string | number>() => {
  const context = useContext(SgPickerInputContext) as SgPickerInputContextValue<T> | null;

  if (!context) {
    throw new Error('useSgPickerInputContext must be used within a SgPickerInputProvider');
  }

  return context;
};

export interface SgPickerInputProviderProps<T extends string | number = string | number> {
  value: SgPickerInputContextValue<T>;
}

/**
 * CONTEXT PROVIDER
 * ================
 *
 * Provides SgPicker context to child components.
 * Supports generic typing to maintain type safety for item values.
 */
export const SgPickerInputProvider = <T extends string | number = string | number>({
  children,
  value,
}: PropsWithChildren<SgPickerInputProviderProps<T>>) => {
  return <SgPickerInputContext.Provider value={value}>{children}</SgPickerInputContext.Provider>;
};
